import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import { type AnalyticsOverview, analyticsOverviewSchema, isSuperAdmin } from '@nbh/shared';
import { useAuth } from '../auth/AuthProvider';
import { CostlyRoomsPanel, RepeatFailuresPanel } from './admin/BreakdownPanels';
import { ExportReportsModal } from './admin/ExportReportsModal';
import { SlaPanel } from './admin/SlaPanel';
import { SuperAdminPortal } from './admin/SuperAdminPortal';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; data: AnalyticsOverview }
  | { kind: 'forbidden' }
  | { kind: 'error' };

function useAnalytics(windowDays: number): LoadState {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    setState({ kind: 'loading' });

    fetch(`/api/analytics/overview?days=${windowDays}`, { signal: controller.signal })
      .then(async (response) => {
        // 401/403 is a normal outcome for a frontline user, not a failure to report.
        if (response.status === 401 || response.status === 403) {
          setState({ kind: 'forbidden' });
          return;
        }
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        setState({ kind: 'ready', data: analyticsOverviewSchema.parse(await response.json()) });
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setState({ kind: 'error' });
      });

    return () => controller.abort();
  }, [windowDays]);

  return state;
}

/**
 * SLA and breakdown dashboard (SPEC.md §5.6). Managers only.
 *
 * The root-tier portal hangs off the same screen but sits outside the analytics
 * branch: losing the analytics call must not take the super-admin controls with it.
 */
export function AdminDashboard({ windowDays = 30 }: { windowDays?: number }) {
  return (
    <div className="space-y-6">
      <AnalyticsSection windowDays={windowDays} />

      {/*
        Decision D23. Renders nothing whatsoever unless the session is SUPER_ADMIN.

        Reaching it also depends on the `/admin` route admitting SUPER_ADMIN at all,
        which App.tsx and AppShell.tsx decide with `hasAdminAuthority` rather than an
        equality check — an exact match would redirect the root account away from a
        page the API happily serves it.
      */}
      <SuperAdminPortal />
    </div>
  );
}

function AnalyticsSection({ windowDays }: { windowDays: number }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const state = useAnalytics(windowDays);
  const [exporting, setExporting] = useState(false);
  const maySeeBreakdowns = isSuperAdmin(user?.role);

  if (state.kind === 'loading') {
    return <p className="text-sm text-slate-400">{t('admin.loading')}</p>;
  }
  // Nothing, deliberately. This branch used to render "Analytics are available to
  // managers only." — an access-denied panel, which is precisely what the requirement
  // forbids: someone who has never seen the dashboard should not be able to tell that a
  // section exists and is being withheld. Following `SuperAdminPortal`, the component
  // decides for itself and renders nothing at all, leaving no border and no gap.
  //
  // The error and loading branches stay. "Could not load" is a network fact, not an
  // authority fact, and hiding it would leave a manager staring at a blank screen with
  // no idea the call had failed.
  if (state.kind === 'forbidden') return null;
  if (state.kind === 'error') {
    return <p className="text-sm text-slate-400">{t('admin.error')}</p>;
  }

  return (
    <div className="space-y-6">
      <SlaPanel sla={state.data.sla} baselineAt={state.data.analyticsBaselineAt} />

      {/*
        Assets, rooms and parts spend are root-tier subjects; the SLA tiles above are
        about tickets and stay for every admin. The server already sends these two arrays
        empty to a non-root caller — that is the control, and it is what keeps the names
        off the wire — so this gate is only about what a non-root admin *sees*: three
        cards that look deliberate, rather than two empty ones explaining themselves.
      */}
      {maySeeBreakdowns && (
        <>
          <RepeatFailuresPanel
            rows={state.data.repeatFailures}
            baselineAt={state.data.analyticsBaselineAt}
          />
          <CostlyRoomsPanel
            rows={state.data.costlyLocations}
            baselineAt={state.data.analyticsBaselineAt}
          />
        </>
      )}

      {/* Last, so on a phone the export sits in the bottom third rather than the header. */}
      <button
        type="button"
        onClick={() => setExporting(true)}
        className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg border border-slate-700 bg-slate-900 px-4 text-sm font-medium text-slate-100"
      >
        <Download className="size-4" aria-hidden="true" />
        {t('export.open')}
      </button>

      {exporting && <ExportReportsModal onClose={() => setExporting(false)} />}
    </div>
  );
}
